import { useActions } from '../providers'
import {
  useCurrentWindowToSelection,
  useIsCurrentWindowAttached,
  useSelectedWindows,
  useWindowSelection,
} from '../store/atoms'
import { useCallback } from 'react'
import { ScreenshotWindow, SelectableContext } from '../types'
import { useDefaultWindowScope } from './useDefaultWindowScope'

export const useAddWindow = () => {
  const { requestContextForWindow, getBrowserTabContext } = useActions()
  const { addSelectedWindow, updateSelectedWindow, isWindowUnselected } = useWindowSelection()
  const selectedWindows = useSelectedWindows()
  const currentWindow = useCurrentWindowToSelection()
  const isCurrentWindowAttached = useIsCurrentWindowAttached()
  const defaultWindowScope = useDefaultWindowScope()

  return useCallback(
    async (window: ScreenshotWindow) => {
      if (typeof requestContextForWindow !== 'function') {
        console.error('Cannot add window, requestContextForWindow undefined')
        return
      }
      if (window.pid === undefined) {
        console.error('Cannot add window, pid undefined')
        return
      }
      if (window.appName === undefined) {
        console.error('Cannot add window, appName undefined')
        return
      }

      // If selecting a window explicitly, also add the focused window.
      // Focused window becomes a snapshot in time, and we stop auto-updating the context.
      if (
        selectedWindows.length === 0 &&
        currentWindow &&
        !isWindowUnselected(currentWindow) &&
        isCurrentWindowAttached &&
        window.pid !== currentWindow.pid &&
        window.windowTitle !== currentWindow.windowTitle &&
        window.appName !== currentWindow.appName
      ) {
        addSelectedWindow(currentWindow)
        // @TODO request context if selectableContext not ready / defined but lastFocusedWindow is? - Josh
      }

      const win = {
        windowTitle: window.windowTitle,
        pid: window.pid,
        appName: window.appName,
      }
      addSelectedWindow({
        ...win,
        appIcon: window.appIcon,
        isLoading: true,
        scope: defaultWindowScope,
      })
      console.time('onAddWindow context')

      let context: SelectableContext

      if (window.pid === -1 && window.tabId !== undefined && getBrowserTabContext) {
        const tabContext = await getBrowserTabContext?.(window.tabId)
        context = {
          appName: '(Extracted from browser tab)',
          isReady: true,
          fullText: tabContext.data.text,
          fullTextSource: 'browserAPI',
          url: tabContext.data.url,
          appIcon: window.appIcon ?? undefined,
          windowTitle: tabContext.data.title,
        }
        updateSelectedWindow(win, {
          context,
          scope: 'browserAPI',
          isLoading: false,
        })
      } else {
        context = await requestContextForWindow(win)
        if (context.fullTextSource === 'browserAPI') {
          updateSelectedWindow(win, {
            context,
            scope: 'browserAPI',
            isLoading: false,
          })
        } else {
          updateSelectedWindow(win, {
            context,
            scope: defaultWindowScope,
            isLoading: false,
          })
        }
      }
      console.timeEnd('onAddWindow context')
      // console.log('context:', win, context)
    },
    [
      addSelectedWindow,
      updateSelectedWindow,
      requestContextForWindow,
      currentWindow,
      selectedWindows,
      isWindowUnselected,
      isCurrentWindowAttached,
      defaultWindowScope,
    ],
  )
}
